import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Chip,
  Switch,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Divider,
  Tooltip,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  useTheme,
  Collapse
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ColorLens as ColorIcon,
  Palette as PaletteIcon,
  ViewDay as ViewDayIcon,
  ViewWeek as ViewWeekIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ViewColumn as ViewColumnIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { getIndexCards, saveIndexCards } from '../../services/storyToolsService';
import ProModal from '../common/ProModal';

const SLUG_LOCATION_TYPES = ['INT.', 'EXT.', 'INT./EXT.', 'I/E.', 'EST.'];
const SLUG_TIME_OF_DAY_OPTIONS = [
  'DAY',
  'NIGHT',
  'MORNING',
  'AFTERNOON',
  'EVENING',
  'DAWN',
  'DUSK',
  'CONT.',
  'LATER',
  'SAME',
  'VAR.'
];

const composeSlugline = ({
  slugLocationType = '',
  slugLocation = '',
  slugTimeOfDay = ''
} = {}) => {
  const type = (slugLocationType || '').trim();
  const location = (slugLocation || '').trim();
  const time = (slugTimeOfDay || '').trim();

  const base = [type, location].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  if (!base) {
    return '';
  }

  return time ? `${base} - ${time}` : base;
};

const parseSlugline = (slugline = '') => {
  const defaultResult = {
    slugLocationType: 'INT.',
    slugLocation: slugline?.trim() || '',
    slugTimeOfDay: 'DAY'
  };

  if (!slugline) return defaultResult;

  const typeRegex = /^(INT\.|EXT\.|INT\/EXT\.|INT\.\/EXT\.|INT\.?\/?EXT\.?|I\/E\.|I\/E|EST\.)/i;
  const timeRegex = /(DAY|NIGHT|MORNING|AFTERNOON|EVENING|DAWN|DUSK|CONT\.|LATER|SAME|VAR\.)(?=$|\s)/i;

  let remaining = slugline.trim();
  let slugLocationType = defaultResult.slugLocationType;
  let slugTimeOfDay = defaultResult.slugTimeOfDay;

  const typeMatch = remaining.match(typeRegex);
  if (typeMatch) {
    slugLocationType = typeMatch[0].toUpperCase().replace('INT./EXT.', 'INT./EXT.');
    remaining = remaining.slice(typeMatch[0].length).trim();
  }

  const dashIndex = remaining.lastIndexOf('-');
  if (dashIndex !== -1) {
    const possibleTime = remaining.slice(dashIndex + 1).trim();
    if (timeRegex.test(possibleTime.toUpperCase())) {
      slugTimeOfDay = possibleTime.toUpperCase();
      remaining = remaining.slice(0, dashIndex).trim();
    }
  }

  return {
    slugLocationType,
    slugLocation: remaining,
    slugTimeOfDay
  };
};

const createDefaultCardState = () => ({
  slugLocationType: 'INT.',
  slugLocation: '',
  slugTimeOfDay: 'DAY',
  notes: '',
  color: 'white'
});

const buildCardFormState = (card) => {
  if (!card) {
    return createDefaultCardState();
  }
  const parsed = parseSlugline(card.slugline);
  return {
    slugLocationType: parsed.slugLocationType || 'INT.',
    slugLocation: (parsed.slugLocation || '').toUpperCase(),
    slugTimeOfDay: parsed.slugTimeOfDay || 'DAY',
    notes: card.notes || '',
    color: card.color || 'white'
  };
};

const IndexCards = ({ projectId, onWideModeChange, wideMode = false }) => {
  const theme = useTheme();
  // State for acts instead of a flat cards array
  const [acts, setActs] = useState([
    { id: "1", name: 'Act 1', cards: [], beats: [] },
    { id: "2", name: 'Act 2a', cards: [], beats: [] },
    { id: "3", name: 'Act 2b', cards: [], beats: [] },
    { id: "4", name: 'Act 3', cards: [], beats: [] },
  ]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedActIndex, setSelectedActIndex] = useState(0);
  const [newCard, setNewCard] = useState(() => createDefaultCardState());
  const [slugMode, setSlugMode] = useState('structured'); // 'structured' | 'manual'
  const slugPreview = useMemo(() => {
    if (slugMode === 'manual') {
      return (newCard.slugline || '').trim();
    }
    return composeSlugline(newCard);
  }, [newCard, slugMode]);
  // Layout states
  const [isVerticalLayout, setIsVerticalLayout] = useState(false);
  const [isTimelineVisible, setIsTimelineVisible] = useState(false);
  // Analytics
  const [totalCards, setTotalCards] = useState(0);
  const [editingActName, setEditingActName] = useState(null);
  const [editingBeatId, setEditingBeatId] = useState(null);
  const actNameInputRef = useRef(null);
  const beatNameInputRef = useRef(null);
  const [beatColorMenuAnchorEl, setBeatColorMenuAnchorEl] = useState(null);
  const [selectedBeat, setSelectedBeat] = useState(null);
  const [selectedBeatActIndex, setSelectedBeatActIndex] = useState(null);
  const [selectedBeatIndex, setSelectedBeatIndex] = useState(null);
  const [beats, setBeats] = useState([]);
  const [resizingBeat, setResizingBeat] = useState(null);
  const [resizingBeatStartX, setResizingBeatStartX] = useState(0);
  const [resizingBeatStartWidth, setResizingBeatStartWidth] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  // Floating toolbar drag state
  // toolbarPos is stored in page coordinates (includes scroll offset)
  const [toolbarPos, setToolbarPos] = useState({ x: 16, y: 16 });
  const [isDraggingToolbar, setIsDraggingToolbar] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // kept for potential future UI; not used in movement now
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, posX: toolbarPos.x, posY: toolbarPos.y, scrollX: 0, scrollY: 0, containerScrollLeft: 0, containerScrollTop: 0 });
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const dragScrollIntervalRef = useRef(null);
  const dragScrollFrameRef = useRef(null);
  const toolbarRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const cardDragScrollFrameRef = useRef(null);
  const lastCardPointerRef = useRef({ x: 0 });
  const getScrollContainer = () => {
    let node = toolbarRef.current?.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      const overflowX = style.overflowX;
      const overflowY = style.overflowY;
      const canScrollX = (overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth;
      const canScrollY = (overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight;
      if (canScrollX || canScrollY) return node;
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };
  const applyToolbarPosition = (pageX, pageY, scrollTarget = getScrollContainer()) => {
    const width = toolbarRef.current?.offsetWidth || 240;
    const height = toolbarRef.current?.offsetHeight || 48;
    const scrollDx = (window.scrollX - dragStartRef.current.scrollX) + ((scrollTarget?.scrollLeft || 0) - dragStartRef.current.containerScrollLeft);
    const scrollDy = (window.scrollY - dragStartRef.current.scrollY) + ((scrollTarget?.scrollTop || 0) - dragStartRef.current.containerScrollTop);
    const dx = (pageX - dragStartRef.current.mouseX) + scrollDx;
    const dy = (pageY - dragStartRef.current.mouseY) + scrollDy;
    const maxX = Math.max(scrollTarget?.scrollWidth || 0, document.documentElement.scrollWidth, document.body?.scrollWidth || 0, window.innerWidth) + width;
    const maxY = Math.max(scrollTarget?.scrollHeight || 0, document.documentElement.scrollHeight, document.body?.scrollHeight || 0, window.innerHeight) + height;
    setToolbarPos({
      x: Math.max(8, Math.min(maxX - width - 8, dragStartRef.current.posX + dx)),
      y: Math.max(8, Math.min(maxY - height - 8, dragStartRef.current.posY + dy))
    });
  };
  const startToolbarDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.touches ? e.touches[0].pageX : e.pageX;
    const clientY = e.touches ? e.touches[0].pageY : e.pageY;
    if (!toolbarRef.current) return;
    const scrollTarget = getScrollContainer();
    scrollContainerRef.current = scrollTarget;
    dragStartRef.current = { mouseX: clientX, mouseY: clientY, posX: toolbarPos.x, posY: toolbarPos.y, scrollX: window.scrollX, scrollY: window.scrollY, containerScrollLeft: scrollTarget?.scrollLeft || 0, containerScrollTop: scrollTarget?.scrollTop || 0 };
    setIsDraggingToolbar(true);
    setDragOffset({ x: 0, y: 0 });
    applyToolbarPosition(clientX, clientY, scrollTarget);

    // start smooth auto-scroll loop
    lastPointerRef.current = { x: clientX, y: clientY };
    const edge = 120;
    const step = () => {
      const vx = lastPointerRef.current.x - window.scrollX;
      const vy = lastPointerRef.current.y - window.scrollY;
      const scrollTargetNode = scrollContainerRef.current || document.scrollingElement || document.documentElement;
      let scrolled = false;
      if (vx > window.innerWidth - edge) {
        scrollTargetNode.scrollLeft = Math.min(scrollTargetNode.scrollWidth, scrollTargetNode.scrollLeft + 12);
        scrolled = true;
      } else if (vx < edge) {
        scrollTargetNode.scrollLeft = Math.max(0, scrollTargetNode.scrollLeft - 12);
        scrolled = true;
      }
      if (vy > window.innerHeight - edge) {
        scrollTargetNode.scrollTop = Math.min(scrollTargetNode.scrollHeight, scrollTargetNode.scrollTop + 12);
        scrolled = true;
      } else if (vy < edge) {
        scrollTargetNode.scrollTop = Math.max(0, scrollTargetNode.scrollTop - 12);
        scrolled = true;
      }
      if (scrolled) {
        // keep toolbar aligned while scrolling
        applyToolbarPosition(lastPointerRef.current.x, lastPointerRef.current.y, scrollTargetNode);
      }
      dragScrollFrameRef.current = requestAnimationFrame(step);
    };
    if (!dragScrollFrameRef.current) {
      dragScrollFrameRef.current = requestAnimationFrame(step);
    }

    const handleMove = (moveEvent) => {
      if (moveEvent.cancelable) moveEvent.preventDefault();
      const mx = moveEvent.touches ? moveEvent.touches[0].pageX : moveEvent.pageX;
      const my = moveEvent.touches ? moveEvent.touches[0].pageY : moveEvent.pageY;
      lastPointerRef.current = { x: mx, y: my };
      applyToolbarPosition(mx, my, scrollTarget);
    };
    const handleUp = () => {
      setIsDraggingToolbar(false);
      if (dragScrollFrameRef.current) {
        cancelAnimationFrame(dragScrollFrameRef.current);
        dragScrollFrameRef.current = null;
      }
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };

    window.addEventListener('mousemove', handleMove, { passive: false });
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp);
  };
  useEffect(() => {
    const saved = localStorage.getItem(`indexCardsToolbarPos_${projectId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          setToolbarPos(parsed);
          return;
        }
      } catch (e) {
        console.warn('Failed to parse toolbar pos', e);
      }
    }
    // fallback default near the top-right of the viewport/header area
    const defaultX = Math.max(16, (typeof window !== 'undefined' ? window.innerWidth - 320 : 16));
    const defaultY = 70;
    setToolbarPos({ x: defaultX, y: defaultY });
  }, [projectId]);
  
  // Drag and drop state for cards
  const [draggedCard, setDraggedCard] = useState(null);
  const [draggedActIndex, setDraggedActIndex] = useState(null);
  const [draggedCardIndex, setDraggedCardIndex] = useState(null);
  const [dragOverActIndex, setDragOverActIndex] = useState(null);
  const [dragOverCardIndex, setDragOverCardIndex] = useState(null);
  
  // Drag and drop state for beats
  const [draggedBeat, setDraggedBeat] = useState(null);
  const [draggedBeatActIndex, setDraggedBeatActIndex] = useState(null);
  const [draggedBeatIndex, setDraggedBeatIndex] = useState(null);
  const [dragOverBeatActIndex, setDragOverBeatActIndex] = useState(null);
  const [dragOverBeatIndex, setDragOverBeatIndex] = useState(null);
  
  // State for menus
  const [colorMenuAnchorEl, setColorMenuAnchorEl] = useState(null);
  // Collapsible acts state
  const [collapsedActs, setCollapsedActs] = useState({});
  const saveTimerRef = useRef(null);

  // Save to API with debouncing and localStorage backup
  const saveToAPI = useCallback(async (actsData, layout, timeline, collapsed) => {
    // Always save to localStorage immediately for offline access
    try {
      localStorage.setItem(`indexCards_${projectId}`, JSON.stringify(actsData));
      localStorage.setItem(`indexCards_layout_${projectId}`, layout);
      localStorage.setItem(`indexCards_timeline_${projectId}`, timeline.toString());
      localStorage.setItem(`indexCards_collapsed_${projectId}`, JSON.stringify(collapsed));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Debounce API save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveIndexCards(projectId, {
          acts: actsData,
          layout: layout,
          timelineVisible: timeline,
          collapsedActs: Object.keys(collapsed).filter(k => collapsed[k])
        });
        console.log('✅ Index cards saved to API');
      } catch (error) {
        console.error('Error saving to API (localStorage backup active):', error);
      }
    }, 1500);
  }, [projectId]);

  // Load acts from API with localStorage fallback
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await getIndexCards(projectId);
        if (response?.data?.indexCards) {
          const { acts: savedActs, layout, timelineVisible, collapsedActs: collapsed } = response.data.indexCards;
          if (savedActs && savedActs.length > 0) {
            setActs(savedActs);
            if (layout) setIsVerticalLayout(layout === 'vertical');
            if (timelineVisible !== undefined) setIsTimelineVisible(timelineVisible);
            if (collapsed) {
              const collapsedObj = {};
              collapsed.forEach(id => { collapsedObj[id] = true; });
              setCollapsedActs(collapsedObj);
            }
            console.log('✅ Loaded index cards from API');
            
            // Update localStorage with API data
            localStorage.setItem(`indexCards_${projectId}`, JSON.stringify(savedActs));
            if (layout) localStorage.setItem(`indexCards_layout_${projectId}`, layout);
            if (timelineVisible !== undefined) localStorage.setItem(`indexCards_timeline_${projectId}`, timelineVisible.toString());
          } else {
            throw new Error('No acts data from API');
          }
        } else {
          throw new Error('No data from API');
        }
      } catch (error) {
        console.log('🔍 No API data, checking localStorage...');
        // Fallback to localStorage
        try {
          const savedActs = localStorage.getItem(`indexCards_${projectId}`);
          if (savedActs) {
            const parsedActs = JSON.parse(savedActs);
            setActs(parsedActs);
            console.log('✅ Loaded acts from localStorage');
            
            // Migrate to API
            const savedLayout = localStorage.getItem(`indexCards_layout_${projectId}`) || 'horizontal';
            const savedTimeline = localStorage.getItem(`indexCards_timeline_${projectId}`) === 'true';
            const savedCollapsed = localStorage.getItem(`indexCards_collapsed_${projectId}`);
            const collapsedObj = savedCollapsed ? JSON.parse(savedCollapsed) : {};
            
            setTimeout(() => {
              saveIndexCards(projectId, {
                acts: parsedActs,
                layout: savedLayout,
                timelineVisible: savedTimeline,
                collapsedActs: Object.keys(collapsedObj).filter(k => collapsedObj[k])
              })
                .then(() => console.log('✅ Migrated localStorage data to API'))
                .catch(err => console.log('Migration to API pending:', err.message));
            }, 2000);
          } else {
            // Initialize with default acts
            const defaultActs = [
              { id: "1", name: 'Act 1', cards: [], beats: [] },
              { id: "2", name: 'Act 2a', cards: [], beats: [] },
              { id: "3", name: 'Act 2b', cards: [], beats: [] },
              { id: "4", name: 'Act 3', cards: [], beats: [] },
            ];
            setActs(defaultActs);
            localStorage.setItem(`indexCards_${projectId}`, JSON.stringify(defaultActs));
          }
          
          const savedLayout = localStorage.getItem(`indexCards_layout_${projectId}`);
          if (savedLayout) setIsVerticalLayout(savedLayout === 'vertical');
          
          const savedTimeline = localStorage.getItem(`indexCards_timeline_${projectId}`);
          if (savedTimeline) setIsTimelineVisible(savedTimeline === 'true');
          
          const savedCollapsed = localStorage.getItem(`indexCards_collapsed_${projectId}`);
          if (savedCollapsed) setCollapsedActs(JSON.parse(savedCollapsed));
        } catch (localError) {
          console.error('Error loading from localStorage:', localError);
        }
      }
      setIsLoaded(true);
    };
    
    loadData();
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [projectId]);
  
  // Save acts whenever they change
  useEffect(() => {
    if (isLoaded) {
      const totalCardsCount = acts.reduce((total, act) => total + act.cards.length, 0);
      setTotalCards(totalCardsCount);
      saveToAPI(acts, isVerticalLayout ? 'vertical' : 'horizontal', isTimelineVisible, collapsedActs);
    }
  }, [acts, projectId, isLoaded, isVerticalLayout, isTimelineVisible, collapsedActs, saveToAPI]);

  const handleToggleActCollapse = (actId) => {
    setCollapsedActs(prev => ({ ...prev, [actId]: !prev[actId] }));
  };
  
  // Function to handle clicking on an act name to edit it
  const handleActNameClick = (actId) => {
    setEditingActName(actId);
    // Focus the input field after a short delay to ensure it's rendered
    setTimeout(() => {
      if (actNameInputRef.current) {
        actNameInputRef.current.focus();
      }
    }, 50);
  };
  
  const handleBeatNameClick = (beatId) => {
    setEditingBeatId(beatId);
    setTimeout(() => {
      if (beatNameInputRef.current) {
        beatNameInputRef.current.focus();
      }
    }, 50);
  };
  
  const handleAddBeat = (actIndex) => {
    const updatedActs = [...acts];
    const newBeat = {
      id: String(Date.now()),
      name: 'New Beat',
      width: 200, // Match the default card width in vertical layout
      color: theme.palette.mode === 'dark' ? '#555555' : '#f0f0f0',
      position: updatedActs[actIndex].beats ? updatedActs[actIndex].beats.length : 0
    };
    
    if (!updatedActs[actIndex].beats) {
      updatedActs[actIndex].beats = [];
    }
    
    updatedActs[actIndex].beats.push(newBeat);
    setActs(updatedActs);
  };

  const handleBeatNameChange = (actIndex, beatIndex, value) => {
    const updatedActs = [...acts];
    if (updatedActs[actIndex].beats && updatedActs[actIndex].beats[beatIndex]) {
      updatedActs[actIndex].beats[beatIndex].name = value;
      setActs(updatedActs);
    }
  };

  const handleBeatColorChange = (actIndex, beatIndex, color) => {
    const updatedActs = [...acts];
    if (updatedActs[actIndex].beats && updatedActs[actIndex].beats[beatIndex]) {
      updatedActs[actIndex].beats[beatIndex].color = color;
      setActs(updatedActs);
    }
  };

  const handleDeleteBeat = (actIndex, beatIndex) => {
    const updatedActs = [...acts];
    if (updatedActs[actIndex].beats && updatedActs[actIndex].beats.length > beatIndex) {
      updatedActs[actIndex].beats.splice(beatIndex, 1);
      setActs(updatedActs);
    }
  };

  const handleBeatResizeStart = (event, actIndex, beatIndex) => {
    // Prevent default behavior and stop propagation
    event.preventDefault();
    event.stopPropagation();
    
    console.log('Beat resize start', { actIndex, beatIndex });
    
    // Ensure we have valid indices
    if (!acts[actIndex] || !acts[actIndex].beats || !acts[actIndex].beats[beatIndex]) {
      console.error('Invalid beat reference');
      return;
    }
    
    // Store the current beat information
    const initialWidth = acts[actIndex].beats[beatIndex].width;
    const initialX = event.clientX;
    
    // Create a resize handler function
    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      
      // Calculate the width change
      const deltaX = moveEvent.clientX - initialX;
      const newWidth = Math.max(50, initialWidth + deltaX);
      
      console.log('Beat resize', { deltaX, newWidth, clientX: moveEvent.clientX });
      
      // Update the beat width
      const updatedActs = JSON.parse(JSON.stringify(acts));
      updatedActs[actIndex].beats[beatIndex].width = newWidth;
      setActs(updatedActs);
    };
    
    // Create a resize end handler
    const handleMouseUp = (upEvent) => {
      upEvent.preventDefault();
      upEvent.stopPropagation();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleDragStart = (e, card, actIndex, cardIndex) => {
    setDraggedCard(card);
    setDraggedActIndex(actIndex);
    setDraggedCardIndex(cardIndex);
    e.dataTransfer.setData('text/plain', JSON.stringify({
      cardId: card.id,
      actIndex,
      cardIndex
    }));
    e.currentTarget.style.opacity = '0.7';
    e.currentTarget.style.transform = 'scale(0.98)';
  };

  const handleDragOver = (e, actIndex, cardIndex = null) => {
    e.preventDefault(); // Necessary to allow dropping
    setDragOverActIndex(actIndex);
    setDragOverCardIndex(cardIndex);
    
    // Add visual feedback for the drop target
    e.currentTarget.style.backgroundColor = 'rgba(144, 202, 249, 0.2)';

    // Auto-scroll horizontally near edges while dragging a card
    const scrollTarget = getScrollContainer();
    const rect = scrollTarget?.getBoundingClientRect?.();
    if (!rect) return;

    const edge = 64; // px threshold from each side
    lastCardPointerRef.current = { x: e.clientX };
    const scrollNode = scrollTarget || document.scrollingElement || document.documentElement;

    const step = () => {
      let scrolled = false;
      const pointerX = lastCardPointerRef.current.x;
      if (pointerX > rect.right - edge) {
        scrollNode.scrollLeft = Math.min(scrollNode.scrollWidth, scrollNode.scrollLeft + 14);
        scrolled = true;
      } else if (pointerX < rect.left + edge) {
        scrollNode.scrollLeft = Math.max(0, scrollNode.scrollLeft - 14);
        scrolled = true;
      }
      if (scrolled) {
        cardDragScrollFrameRef.current = requestAnimationFrame(step);
      } else {
        if (cardDragScrollFrameRef.current) {
          cancelAnimationFrame(cardDragScrollFrameRef.current);
          cardDragScrollFrameRef.current = null;
        }
      }
    };

    if (!cardDragScrollFrameRef.current) {
      cardDragScrollFrameRef.current = requestAnimationFrame(step);
    }
  };
  
  const handleDragLeave = (e) => {
    // Remove visual feedback
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  const handleDrop = (e, targetActIndex, targetCardIndex = null) => {
    e.preventDefault();
    // Prevent the drop event from bubbling to parent containers which could
    // also handle onDrop and unintentionally append the card to the end.
    e.stopPropagation();
    
    if (!draggedCard || draggedActIndex === null) {
      console.log('No card being dragged');
      return;
    }
    
    console.log('Drop:', { 
      draggedCard, 
      fromAct: draggedActIndex, 
      toAct: targetActIndex,
      fromIndex: draggedCardIndex,
      toIndex: targetCardIndex
    });
    
    // Create a deep copy of the acts array
    const updatedActs = JSON.parse(JSON.stringify(acts));
    
    // Get the source and destination acts
    const sourceAct = updatedActs[draggedActIndex];
    const targetAct = updatedActs[targetActIndex];
    
    if (!sourceAct || !targetAct) {
      console.error('Source or target act not found');
      return;
    }
    
    // Find the card in the source act
    const cardIndex = sourceAct.cards.findIndex(card => card.id === draggedCard.id);
    if (cardIndex === -1) {
      console.error('Card not found in source act');
      return;
    }
    
    // Remove the card from the source act
    const [movedCard] = sourceAct.cards.splice(cardIndex, 1);

    // When targetCardIndex is provided, treat it as the desired insertion index
    if (targetCardIndex !== null) {
      const isSameAct = sourceAct === targetAct;
      let insertIndex = targetCardIndex;
      // If moving within the same act and the original index was before the insertion
      // point, the removal shifts the insertion point left by 1.
      if (isSameAct && cardIndex < insertIndex) {
        insertIndex -= 1;
      }
      insertIndex = Math.max(0, Math.min(insertIndex, targetAct.cards.length));
      targetAct.cards.splice(insertIndex, 0, movedCard);
    } else {
      // Fallback: append to end
      targetAct.cards.push(movedCard);
    }
    
    // Update the state
    setActs(updatedActs);
    
    // Reset drag state
    setDraggedCard(null);
    setDraggedActIndex(null);
    setDraggedCardIndex(null);
    setDragOverActIndex(null);
    setDragOverCardIndex(null);

    if (cardDragScrollFrameRef.current) {
      cancelAnimationFrame(cardDragScrollFrameRef.current);
      cardDragScrollFrameRef.current = null;
    }
    
    // Remove visual styles
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  const handleDragEnd = (e) => {
    // Reset drag state if drop didn't occur
    setDraggedCard(null);
    setDraggedActIndex(null);
    setDraggedCardIndex(null);
    setDragOverActIndex(null);
    setDragOverCardIndex(null);

    if (cardDragScrollFrameRef.current) {
      cancelAnimationFrame(cardDragScrollFrameRef.current);
      cardDragScrollFrameRef.current = null;
    }
    
    // Remove visual styles
    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.transform = 'none';
  };

  const handleBeatDragStart = (e, beat, actIndex, beatIndex) => {
    console.log('Beat drag start:', { beat, actIndex, beatIndex });
    setDraggedBeat(beat);
    setDraggedBeatActIndex(actIndex);
    setDraggedBeatIndex(beatIndex);
    
    // Set data for transfer
    e.dataTransfer.setData('text/plain', JSON.stringify({
      beatId: beat.id,
      actIndex: actIndex,
      beatIndex: beatIndex
    }));
    
    // Add visual effect to dragged element
    e.currentTarget.style.opacity = '0.7';
    e.currentTarget.style.transform = 'scale(0.98)';
  };

  const handleBeatDragOver = (e, actIndex, beatIndex = null) => {
    e.preventDefault(); // Necessary to allow dropping
    setDragOverBeatActIndex(actIndex);
    setDragOverBeatIndex(beatIndex);
    
    // Add visual feedback for the drop target
    e.currentTarget.style.backgroundColor = 'rgba(144, 202, 249, 0.2)';
  };
  
  const handleBeatDragLeave = (e) => {
    // Remove visual feedback
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  const handleBeatDrop = (e, targetActIndex, targetBeatIndex = null) => {
    e.preventDefault();
    // Prevent bubbling to parent containers
    e.stopPropagation();
    
    if (!draggedBeat || draggedBeatActIndex === null) {
      console.log('No beat being dragged');
      return;
    }
    
    console.log('Beat Drop:', { 
      draggedBeat, 
      fromAct: draggedBeatActIndex, 
      toAct: targetActIndex,
      fromIndex: draggedBeatIndex,
      toIndex: targetBeatIndex
    });
    
    // Create a deep copy of the acts array
    const updatedActs = JSON.parse(JSON.stringify(acts));
    
    // Get the source and destination acts
    const sourceAct = updatedActs[draggedBeatActIndex];
    const targetAct = updatedActs[targetActIndex];
    
    if (!sourceAct || !targetAct) {
      console.error('Source or target act not found');
      return;
    }
    
    // Find the beat in the source act
    const beatIndex = sourceAct.beats.findIndex(beat => beat.id === draggedBeat.id);
    if (beatIndex === -1) {
      console.error('Beat not found in source act');
      return;
    }
    
    // Remove the beat from the source act
    const [movedBeat] = sourceAct.beats.splice(beatIndex, 1);
    
    // Add the beat to the target act at the specified position
    if (targetBeatIndex !== null) {
      const isSameAct = sourceAct === targetAct;
      const wasDroppingOnLastBeat = targetBeatIndex === targetAct.beats.length - 1;
      let insertIndex;
      if (isSameAct) {
        if (targetBeatIndex > beatIndex) {
          // Moving downward: after target
          insertIndex = wasDroppingOnLastBeat ? targetAct.beats.length : targetBeatIndex;
        } else if (targetBeatIndex < beatIndex) {
          // Moving upward: before target
          insertIndex = targetBeatIndex;
        } else {
          insertIndex = beatIndex;
        }
      } else {
        insertIndex = targetBeatIndex;
      }
      targetAct.beats.splice(Math.max(0, insertIndex), 0, movedBeat);
    } else {
      // If dropping onto the act container, append to the end
      targetAct.beats.push(movedBeat);
    }
    
    // Update positions for all beats in the target act
    targetAct.beats.forEach((beat, idx) => {
      beat.position = idx;
    });
    
    // If source act is different from target act, update positions in source act too
    if (sourceAct !== targetAct) {
      sourceAct.beats.forEach((beat, idx) => {
        beat.position = idx;
      });
    }
    
    // Update the state
    setActs(updatedActs);
    
    // Reset drag state
    setDraggedBeat(null);
    setDraggedBeatActIndex(null);
    setDraggedBeatIndex(null);
    setDragOverBeatActIndex(null);
    setDragOverBeatIndex(null);
    
    // Remove visual styles
    e.currentTarget.style.backgroundColor = 'transparent';
  };

  const handleBeatDragEnd = (e) => {
    // Reset drag state if drop didn't occur
    setDraggedBeat(null);
    setDraggedBeatActIndex(null);
    setDraggedBeatIndex(null);
    setDragOverBeatActIndex(null);
    setDragOverBeatIndex(null);
    
    // Remove visual styles
    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.transform = 'none';
  };

  const handleAddCard = () => {
    if (!(newCard.slugLocation || '').trim()) {
      return;
    }

    const updatedActs = [...acts];
    const targetAct = updatedActs[selectedActIndex];

    if (!targetAct) {
      console.error('Target act not found');
      return;
    }

    // Create a new card with a unique string ID
    const slugline = composeSlugline(newCard);
    const newCardWithId = {
      ...newCard,
      slugline,
      id: String(Date.now()),
      createdAt: new Date().toISOString()
    };

    // Add the new card to the target act
    targetAct.cards.push(newCardWithId);

    // Update the state
    setActs(updatedActs);
    setOpenDialog(false);
    
    // Reset the new card form
    setNewCard(createDefaultCardState());
  };

  const handleUpdateCard = () => {
    if (!selectedCard || selectedActIndex === null) {
      return;
    }

    const updatedActs = [...acts];
    const targetAct = updatedActs[selectedActIndex];

    if (!targetAct) {
      console.error('Target act not found');
      return;
    }

    // Find the card in the target act
    const cardIndex = targetAct.cards.findIndex(card => card.id === selectedCard.id);
    
    if (cardIndex === -1) {
      console.error('Card not found in target act');
      return;
    }

    // Update the card
    const slugline = composeSlugline(newCard);
    targetAct.cards[cardIndex] = {
      ...targetAct.cards[cardIndex],
      slugline,
      slugLocationType: newCard.slugLocationType,
      slugLocation: newCard.slugLocation,
      slugTimeOfDay: newCard.slugTimeOfDay,
      notes: newCard.notes,
      color: newCard.color,
      updatedAt: new Date().toISOString()
    };

    // Update the state
    setActs(updatedActs);
    setOpenDialog(false);
    setSelectedCard(null);
    
    // Reset the new card form
    setNewCard(createDefaultCardState());
  };

  const handleDeleteCard = (cardId = selectedCard?.id, actIndexOverride = selectedActIndex) => {
    if (!cardId || actIndexOverride === null || actIndexOverride === undefined) {
      return;
    }

    const updatedActs = [...acts];
    const targetAct = updatedActs[actIndexOverride];

    if (!targetAct) {
      console.error('Target act not found');
      return;
    }

    // Find the card in the target act
    const cardIndex = targetAct.cards.findIndex(card => card.id === cardId);

    if (cardIndex === -1) {
      console.error('Card not found in target act');
      return;
    }

    // Remove the card
    targetAct.cards.splice(cardIndex, 1);

    // Update the state
    setActs(updatedActs);
    setSelectedCard(null);
  };

  const handleChangeCardColor = (color) => {
    if (!selectedCard || selectedActIndex === null) {
      return;
    }

    const updatedActs = [...acts];
    const targetAct = updatedActs[selectedActIndex];

    if (!targetAct) {
      console.error('Target act not found');
      return;
    }

    // Find the card in the target act
    const cardIndex = targetAct.cards.findIndex(card => card.id === selectedCard.id);
    
    if (cardIndex === -1) {
      console.error('Card not found in target act');
      return;
    }

    // Update the card color
    targetAct.cards[cardIndex] = {
      ...targetAct.cards[cardIndex],
      color: color,
      updatedAt: new Date().toISOString()
    };

    // Update the state
    setActs(updatedActs);
    setColorMenuAnchorEl(null);
  };

  const handleAddAct = () => {
    const newAct = {
      id: Date.now(),
      name: `Act ${acts.length + 1}`,
      cards: [],
      beats: []
    };
    setActs([...acts, newAct]);
  };

  const handleDeleteAct = (actIndex) => {
    if (acts.length <= 1) {
      alert('You must have at least one act');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${acts[actIndex].name}"? All cards in this act will be deleted.`)) {
      const updatedActs = [...acts];
      updatedActs.splice(actIndex, 1);
      setActs(updatedActs);
    }
  };

  const handleActNameEdit = (actIndex) => {
    setEditingActName(actIndex);
    setTimeout(() => {
      if (actNameInputRef.current) {
        actNameInputRef.current.focus();
      }
    }, 50);
  };

  const handleActNameChange = (actIndex, newName) => {
    const updatedActs = [...acts];
    updatedActs[actIndex].name = newName;
    setActs(updatedActs);
  };

  const handleActNameBlur = () => {
    setEditingActName(null);
  };

  const handleActNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      setEditingActName(null);
    }
  };

  const handleLayoutToggle = () => {
    setIsVerticalLayout((prev) => !prev);
    onWideModeChange && onWideModeChange(!isVerticalLayout);
  };

  const handleTimelineToggle = () => {
    setIsTimelineVisible(!isTimelineVisible);
  };

  useEffect(() => {
    const saveLayoutPreferences = () => {
      localStorage.setItem(`layout_${projectId}`, isVerticalLayout ? 'vertical' : 'horizontal');
      localStorage.setItem(`timeline_${projectId}`, isTimelineVisible.toString());
    };
    saveLayoutPreferences();
  }, [isVerticalLayout, isTimelineVisible]);

  useEffect(() => {
    if (typeof onWideModeChange === 'function') {
      onWideModeChange(isVerticalLayout);
    }
  }, [isVerticalLayout]);

  const getContrastTextColor = (backgroundColor, alpha = 1) => {
    // Convert hex to RGB
    let r, g, b;
    if (backgroundColor.startsWith('#')) {
      const hex = backgroundColor.substring(1);
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (backgroundColor.startsWith('rgb')) {
      const matches = backgroundColor.match(/\d+/g);
      if (matches && matches.length >= 3) {
        r = parseInt(matches[0]);
        g = parseInt(matches[1]);
        b = parseInt(matches[2]);
      } else {
        return 'rgba(0, 0, 0, ' + alpha + ')'; // Default to black if parsing fails
      }
    } else {
      return 'rgba(0, 0, 0, ' + alpha + ')'; // Default to black for unknown formats
    }
  
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
    // Return white for dark colors, black for light colors
    return luminance > 0.5 ? 
      'rgba(0, 0, 0, ' + alpha + ')' : 
      'rgba(255, 255, 255, ' + alpha + ')';
  };

  return (
    <Box sx={{ 
      p: 3, 
      position: 'relative',
      width: '100%',
      height: 'auto',
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible',
      maxHeight: 'none'
    }}>
      {/* Modern Header with Gradient Badge */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.primary.main
            }}
          >
            <TimelineIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Story Index Cards
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              {totalCards} cards across {acts.length} acts
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box
            sx={{
              position: 'fixed',
              left: typeof window !== 'undefined' ? toolbarPos.x - window.scrollX : toolbarPos.x,
              top: typeof window !== 'undefined' ? toolbarPos.y - window.scrollY : toolbarPos.y,
              zIndex: 4000,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              borderRadius: 0,
              border: (theme) => `1px solid ${alpha(
                theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
                theme.palette.mode === 'dark' ? 0.15 : 0.1
              )}`,
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.common.black, 0.04),
              px: 1,
              py: 0.5,
              cursor: isDraggingToolbar ? 'grabbing' : 'grab',
              userSelect: 'none',
              pointerEvents: 'auto',
              touchAction: 'none'
            }}
            ref={toolbarRef}
            onMouseMove={(e) => isDraggingToolbar && applyToolbarPosition(e.clientX, e.clientY)}
            onTouchMove={(e) => {
              if (!isDraggingToolbar) return;
              const t = e.touches?.[0];
              if (t) applyToolbarPosition(t.clientX, t.clientY);
            }}
            onMouseUp={() => setIsDraggingToolbar(false)}
            onTouchEnd={() => setIsDraggingToolbar(false)}
          >
            <Box
              onMouseDown={startToolbarDrag}
              onTouchStart={startToolbarDrag}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 1,
                color: 'text.secondary',
                cursor: isDraggingToolbar ? 'grabbing' : 'grab',
                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) }
              }}
              aria-label="Drag toolbar"
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
            <Tooltip title={isVerticalLayout ? "Switch to horizontal layout" : "Switch to vertical layout"} arrow>
              <IconButton 
                onClick={handleLayoutToggle}
                size="small"
                sx={{
                  color: isVerticalLayout ? theme.palette.primary.main : theme.palette.text.primary,
                  borderRadius: 1
                }}
              >
                {isVerticalLayout ? <ViewDayIcon fontSize="small" /> : <ViewWeekIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title={isTimelineVisible ? "Hide timeline" : "Show timeline"} arrow>
              <span>
                <IconButton
                  onClick={handleTimelineToggle}
                  disabled={!isVerticalLayout}
                  size="small"
                  sx={{
                    color: isTimelineVisible ? theme.palette.primary.main : theme.palette.text.primary,
                    borderRadius: 1,
                    '&.Mui-disabled': {
                      color: alpha(theme.palette.text.disabled, 0.7)
                    }
                  }}
                >
                  <TimelineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            
            <Box sx={{ width: 1, height: 28, mx: 0.5, bgcolor: (theme) => alpha(theme.palette.text.primary, 0.1) }} />
            
            <Tooltip title="Add a new act" arrow>
              <span>
                <IconButton
                  onClick={handleAddAct}
                  aria-label="Add act"
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.text.primary, 0.1)
                    }
                  }}
                >
                  <ViewColumnIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Add a new card" arrow>
              <span>
                <IconButton
                  onClick={() => {
                    setSelectedCard(null);
                    setSelectedActIndex(0);
                    setNewCard(createDefaultCardState());
                    setOpenDialog(true);
                  }}
                  aria-label="Add card"
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    color: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15)
                    }
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: isVerticalLayout ? 'column' : 'row',
        gap: 3,
        overflowX: 'visible',
        overflowY: 'visible',
        pb: 2,
        mt: 2,
        maxHeight: 'none',
        height: 'auto',
        flex: 1,
        flexWrap: isVerticalLayout ? 'nowrap' : 'wrap'
      }}>
        {acts.map((act, actIndex) => (
          <Box 
            key={act.id} 
            sx={{ 
              mb: isVerticalLayout ? 3 : 0,
              minWidth: (isVerticalLayout || totalCards === 0) ? '100%' : '300px',
              maxWidth: (isVerticalLayout || totalCards === 0) ? '100%' : '300px',
              width: (isVerticalLayout || totalCards === 0) ? '100%' : '320px',
              flex: (isVerticalLayout || totalCards === 0) ? '1 0 auto' : '0 0 320px',
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.7)' : 'rgba(255, 255, 255, 0.9)',
              borderRadius: 0,
              p: 2,
              position: 'relative',
              border: '1px solid',
              borderColor: 'divider'
            }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2,
              pb: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={() => handleToggleActCollapse(act.id)}>
                  {collapsedActs[act.id] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              {editingActName === actIndex ? (
                <TextField
                  value={act.name}
                  onChange={(e) => handleActNameChange(actIndex, e.target.value)}
                  autoFocus
                  onBlur={() => setEditingActName(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingActName(null);
                    }
                  }}
                  inputRef={actNameInputRef}
                  variant="standard"
                  sx={{ 
                    width: '100%',
                    '& .MuiInputBase-input': {
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      letterSpacing: '-0.5px'
                    }
                  }}
                />
              ) : (
                <Typography 
                  variant="h6" 
                  onClick={() => handleActNameClick(act.id)}
                  sx={{ 
                    fontWeight: 600,
                    cursor: 'pointer',
                    letterSpacing: '-0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                    '&:hover': { color: theme.palette.primary.main }
                  }}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      mr: 1
                    }}
                  >
                    {actIndex + 1}
                  </Box>
                  {act.name}
                </Typography>
              )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Add timeline beat">
                  <span>
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleAddBeat(actIndex)}
                    >
                      <TimelineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete this act">
                  <span>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleDeleteAct(actIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            
            <Collapse in={!collapsedActs[act.id]} unmountOnExit>
            {/* Combined Beats and Cards Section */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', // Always column now, beats on top of cards
              flexWrap: 'nowrap',
              overflowX: 'visible', // Let width grow with cards
              overflowY: 'visible', // Let the parent grow vertically instead of internal scrolling
              flex: 1, // Take up available space
              maxHeight: 'none', 
              pb: isVerticalLayout ? 1 : 0,
              backgroundColor: dragOverActIndex === actIndex && dragOverCardIndex === null 
                ? theme.palette.mode === 'dark' 
                  ? 'rgba(144, 202, 249, 0.15)' 
                  : 'rgba(144, 202, 249, 0.2)' 
                : 'transparent',
              transition: 'background-color 0.2s ease',
              borderRadius: 1,
              p: 1,
              minHeight: '200px', // Ensure it has some height for drop zones
              width: isVerticalLayout ? 'max-content' : '100%',
              minWidth: '100%'
            }}
            // Allow dropping cards into an act in both layouts
            onDragOver={(e) => handleDragOver(e, actIndex)} 
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, actIndex)}
            >
              {/* Timeline Beats Section - Moved inside */}
              {isVerticalLayout && isTimelineVisible && (
                <Box sx={{ 
                  mb: 2,
                  p: 1.5,
                  borderRadius: 0,
                  bgcolor: (theme) => theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.06),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  alignItems: 'center',
                  flexShrink: 0
                }}>
                  {act.beats && act.beats.length > 0 ? (
                    act.beats.map((beat, beatIndex) => (
                      <Box 
                        key={beat.id}
                        sx={{
                          p: 1.1,
                          borderRadius: 1,
                          bgcolor: beat.color || (theme.palette.mode === 'dark' ? '#30343a' : '#f7f9fc'),
                          minWidth: `${beat.width}px`,
                          height: '44px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          position: 'relative',
                          border: '1px solid',
                          borderColor: theme => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.06)
                        }}
                        // Add beat drag handlers if needed in future
                      >
                        {editingBeatId === beat.id ? (
                          <TextField
                            value={beat.name}
                            onChange={(e) => handleBeatNameChange(actIndex, beatIndex, e.target.value)}
                            autoFocus
                            onBlur={() => setEditingBeatId(null)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setEditingBeatId(null);
                              }
                            }}
                            inputRef={beatNameInputRef}
                            variant="standard"
                            size="small"
                            sx={{ 
                              maxWidth: `${beat.width - 48}px`,
                              '& .MuiInputBase-input': {
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                letterSpacing: '-0.1px',
                                color: getContrastTextColor(beat.color || (theme.palette.mode === 'dark' ? '#30343a' : '#f7f9fc')),
                                padding: '4px 6px',
                              },
                              '& .MuiInput-underline:before': {
                                borderBottomColor: getContrastTextColor(beat.color || (theme.palette.mode === 'dark' ? '#30343a' : '#f7f9fc')) + '40'
                              },
                              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                borderBottomColor: getContrastTextColor(beat.color || (theme.palette.mode === 'dark' ? '#30343a' : '#f7f9fc'))
                              }
                            }}
                          />
                        ) : (
                          <Typography 
                            variant="caption" 
                            onClick={() => handleBeatNameClick(beat.id)}
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: `${beat.width - 40}px`,
                              color: getContrastTextColor(beat.color || (theme.palette.mode === 'dark' ? '#30343a' : '#f7f9fc')),
                              fontWeight: 600,
                              letterSpacing: '-0.1px',
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {beat.name}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', position: 'absolute', right: 6, top: 6, gap: 0.5 }}>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              padding: 0.35,
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              color: getContrastTextColor(beat.color || (theme.palette.mode === 'dark' ? '#30343a' : '#f7f9fc')),
                              backgroundColor: 'rgba(255,255,255,0.12)',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.18)',
                                transform: 'translateY(-1px)'
                              }
                            }}
                            onClick={(e) => {
                              setSelectedBeat(beat);
                              setSelectedBeatActIndex(actIndex);
                              setSelectedBeatIndex(beatIndex);
                              setBeatColorMenuAnchorEl(e.currentTarget);
                            }}
                          >
                            <PaletteIcon sx={{ fontSize: 16, opacity: 0.9 }} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              padding: 0.35,
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              color: getContrastTextColor(beat.color || (theme.palette.mode === 'dark' ? '#30343a' : '#f7f9fc')),
                              backgroundColor: 'rgba(255,255,255,0.12)',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.18)',
                                transform: 'translateY(-1px)'
                              }
                            }}
                            onClick={() => handleDeleteBeat(actIndex, beatIndex)}
                          >
                            <DeleteIcon sx={{ fontSize: 16, opacity: 0.9 }} />
                          </IconButton>
                        </Box>
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            right: 0, 
                            top: 0, 
                            bottom: 0, 
                            width: '8px', 
                            cursor: 'ew-resize',
                            '&:hover': { 
                              backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' 
                            } 
                          }}
                          onMouseDown={(e) => handleBeatResizeStart(e, actIndex, beatIndex)}
                        />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" sx={{ 
                      fontStyle: 'italic', 
                      p: 1,
                      color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'
                    }}>
                      No beats added. Click the timeline icon to add beats.
                    </Typography>
                  )}
                  <Tooltip title="Add a beat">
                    <IconButton 
                      size="small" 
                      onClick={() => handleAddBeat(actIndex)}
                      sx={{ 
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                        color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'primary.main',
                        '&:hover': { 
                          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.3)' : 'rgba(25, 118, 210, 0.2)',
                          color: theme => theme.palette.mode === 'dark' ? '#fff' : 'primary.dark'
                        }
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              
              {/* Cards Row Container */}
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: isVerticalLayout ? 'row' : 'column',
                  gap: 1,
                  minHeight: isVerticalLayout ? 'auto' : '200px', // Ensure empty acts have height in horizontal layout
                  width: isVerticalLayout ? 'max-content' : '100%',
                  minWidth: '100%',
                  // overflowX: 'auto', // Removed, parent (combined container) handles this for vertical layout
                  // overflowY: isVerticalLayout ? 'visible' : 'auto', // Parent (combined container) handles this for non-vertical
                  flex: 1, // Allows this row to take up space if needed
                  // backgroundColor: 'rgba(255,0,0,0.1)' // For debugging layout
                }}
              >
                {/* Leading drop zone (insert at index 0) */}
                <Box
                  sx={{
                    width: isVerticalLayout ? '3px' : '100%',
                    height: isVerticalLayout ? '100%' : '12px',
                    alignSelf: isVerticalLayout ? 'stretch' : 'auto',
                    backgroundColor: dragOverActIndex === actIndex && dragOverCardIndex === 0 ? 'rgba(144, 202, 249, 0.3)' : 'transparent'
                  }}
                  onDragOver={(e) => handleDragOver(e, actIndex, 0)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, actIndex, 0)}
                />
                {act.cards.map((card, cardIndex) => (
                  <React.Fragment key={card.id}>
                  <Box 
                    sx={{ 
                      width: isVerticalLayout ? '250px' : 'auto', // Match the card width in vertical layout
                      flex: isVerticalLayout ? '0 0 auto' : '1',
                      opacity: draggedCard && draggedCard.id === card.id ? 0.7 : 1,
                      transform: draggedCard && draggedCard.id === card.id ? 'scale(0.98)' : 'scale(1)',
                      transition: 'opacity 0.2s ease, transform 0.2s ease',
                      backgroundColor: dragOverActIndex === actIndex && dragOverCardIndex === cardIndex ? 'rgba(144, 202, 249, 0.2)' : 'transparent',
                      mb: 0, // No extra margin between stacked cards
                      mr: 0  // No extra margin between cards in vertical layout
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, card, actIndex, cardIndex)}
                    onDragOver={(e) => handleDragOver(
                      e,
                      actIndex,
                      (draggedActIndex === actIndex && draggedCardIndex !== null && draggedCardIndex < cardIndex)
                        ? cardIndex + 1
                        : cardIndex
                    )}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(
                      e,
                      actIndex,
                      (draggedActIndex === actIndex && draggedCardIndex !== null && draggedCardIndex < cardIndex)
                        ? cardIndex + 1
                        : cardIndex
                    )}
                    onDragEnd={handleDragEnd}
                  >
                    <Paper
                      elevation={draggedCard && draggedCard.id === card.id ? 6 : 0}
                      sx={{
                        p: 1.25,
                        backgroundColor: card.color,
                        position: 'relative',
                        // Standard index card proportions (3x5 inches)
                        height: isVerticalLayout ? '150px' : '150px', // Fixed height in both layouts
                        transition: 'all 0.2s ease',
                        width: isVerticalLayout ? '250px' : 'auto', // Fixed width in vertical layout (3:5 ratio)
                        color: getContrastTextColor(card.color),
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: draggedCard && draggedCard.id === card.id ? 
                          (theme => theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.15)') : 
                          (theme => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)'),
                        '&:hover': {
                          boxShadow: theme => theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.15)',
                          transform: 'translateY(-4px)'
                        },
                        animation: cardIndex === act.cards.length - 1 ? 'fadeIn 0.5s ease-in-out' : 'none',
                        '@keyframes fadeIn': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateY(10px)'
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateY(0)'
                          }
                        },
                        '&:hover .card-delete-button': {
                          opacity: 1
                        }
                      }}
                      onClick={() => {
                        setSelectedCard(card);
                        setSelectedActIndex(actIndex);
                        setNewCard({
                          slugline: composeSlugline(parseSlugline(card.slugline)),
                          slugLocationType: parseSlugline(card.slugline).slugLocationType,
                          slugLocation: parseSlugline(card.slugline).slugLocation,
                          slugTimeOfDay: parseSlugline(card.slugline).slugTimeOfDay,
                          notes: card.notes || '',
                          color: card.color || 'white'
                        });
                        setOpenDialog(true);
                      }}
                    >
                      <IconButton
                        size="small"
                        className="card-delete-button"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          opacity: 0,
                          transition: 'opacity 0.2s ease, background-color 0.2s ease',
                          bgcolor: 'rgba(211, 47, 47, 0.15)',
                          color: '#fff',
                          '&:hover': {
                            bgcolor: 'rgba(211, 47, 47, 0.35)'
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCard(card.id, actIndex);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight={700} 
                        sx={{ 
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: '0.35px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: 0.3,
                          pr: 3,
                          opacity: 0.85,
                          fontFamily: `'Courier Prime', 'Courier New', Courier, monospace`
                        }}
                      >
                        {card.slugline}
                      </Typography>
                      <Divider sx={{ my: 0.8, borderColor: 'rgba(0,0,0,0.35)' }} />
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Typography 
                          variant="body2"
                          sx={{
                            fontSize: '0.7rem',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            overflowY: 'auto',
                            maxHeight: '80px',
                            pr: 0.5,
                            fontFamily: `'Courier Prime', 'Courier New', Courier, monospace`
                          }}
                        >
                          {card.notes}
                        </Typography>
                      </Box>
                      <Box />
                    </Paper>
                  </Box>
                  {/* Between-card drop zone: insert at index cardIndex + 1 */}
                  <Box
                    sx={{
                      width: isVerticalLayout ? '3px' : '100%',
                      height: isVerticalLayout ? '100%' : '12px',
                      alignSelf: isVerticalLayout ? 'stretch' : 'auto',
                      backgroundColor: dragOverActIndex === actIndex && dragOverCardIndex === cardIndex + 1 ? 'rgba(144, 202, 249, 0.3)' : 'transparent'
                    }}
                    onDragOver={(e) => handleDragOver(e, actIndex, cardIndex + 1)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, actIndex, cardIndex + 1)}
                  />
                  </React.Fragment>
                ))}
                {/* Trailing end drop zone (insert at end) */}
                {act.cards.length > 0 && (
                  <Box
                    sx={{
                      width: isVerticalLayout ? '8px' : '100%',
                      height: isVerticalLayout ? '100%' : '16px',
                      alignSelf: isVerticalLayout ? 'stretch' : 'auto',
                      backgroundColor:
                        dragOverActIndex === actIndex && dragOverCardIndex === act.cards.length
                          ? 'rgba(144, 202, 249, 0.3)'
                          : 'transparent'
                    }}
                    onDragOver={(e) => handleDragOver(e, actIndex, act.cards.length)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, actIndex, act.cards.length)}
                  />
                )}
                {act.cards.length === 0 && (
                  <Box 
                    sx={{ 
                      width: isVerticalLayout ? '200px' : 'auto',
                      height: isVerticalLayout ? '120px' : '100px',
                      border: '2px dashed #ccc',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999',
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      p: 2,
                      textAlign: 'center',
                      fontSize: '0.875rem'
                    }}
                  >
                    Drop cards here or click "Add Card" to create a new one
                  </Box>
                )}
              </Box>
              <Tooltip title="Add a new card to this act">
                <span>
                  <Button 
                    onClick={() => {
                      setSelectedCard(null);
                      setSelectedActIndex(actIndex);
                      setNewCard(createDefaultCardState());
                      setOpenDialog(true);
                    }}
                    sx={{ mt: 2, width: '100%' }}
                  >
                    Add Card
                  </Button>
                </span>
              </Tooltip>
            </Box>
            </Collapse>
          </Box>
        ))}
      </Box>

      <ProModal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={selectedCard ? 'Edit Card' : 'Add New Card'}
        eyebrow="INDEX CARD"
        icon={<ColorIcon />}
        actions={(
          <>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={selectedCard ? handleUpdateCard : handleAddCard}
              disabled={!slugPreview.trim()}
              variant="contained"
            >
              {selectedCard ? 'Update' : 'Add'}
            </Button>
          </>
        )}
      >
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Slugline Details
            </Typography>
            <Switch
              size="small"
              checked={slugMode === 'manual'}
              onChange={(e) => setSlugMode(e.target.checked ? 'manual' : 'structured')}
            />
            <Typography variant="body2" color="text.secondary">
              {slugMode === 'manual' ? 'Manual' : 'Structured'}
            </Typography>
          </Box>
          {slugMode === 'structured' ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>INT/EXT</InputLabel>
                    <Select
                      value={newCard.slugLocationType}
                      label="INT/EXT"
                      onChange={(e) => setNewCard({ ...newCard, slugLocationType: e.target.value })}
                    >
                      {SLUG_LOCATION_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Primary Location"
                    size="small"
                    fullWidth
                    value={newCard.slugLocation}
                    onChange={(e) => setNewCard({ ...newCard, slugLocation: e.target.value.toUpperCase() })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Time of Day</InputLabel>
                    <Select
                      value={newCard.slugTimeOfDay}
                      label="Time of Day"
                      onChange={(e) => setNewCard({ ...newCard, slugTimeOfDay: e.target.value })}
                    >
                      {SLUG_TIME_OF_DAY_OPTIONS.map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Chip
                label={slugPreview || 'INT. MAIN STREET - DAY'}
                color={slugPreview ? 'primary' : 'default'}
                variant={slugPreview ? 'filled' : 'outlined'}
                sx={{ mb: 3, fontWeight: 600 }}
              />
            </>
          ) : (
            <TextField
              label="Slugline"
              fullWidth
              margin="dense"
              value={newCard.slugline || ''}
              onChange={(e) => setNewCard({ ...newCard, slugline: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            margin="dense"
            label="Notes"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={newCard.notes}
            onChange={(e) => setNewCard({...newCard, notes: e.target.value})}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {['white', '#ffcccc', '#ccffcc', '#ccccff', '#ffffcc', '#ffccff', '#ccffff'].map(color => (
              <Tooltip key={color} title={color === 'white' ? 'White' : color}>
                <span>
                  <Box
                    onClick={() => setNewCard({...newCard, color})}
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: color,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 0,
                      cursor: 'pointer'
                    }}
                  />
                </span>
              </Tooltip>
            ))}
            <Tooltip title="Custom color">
              <span>
                <Box
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'color';
                    input.value = newCard.color === 'white' ? '#ffffff' : newCard.color;
                    input.addEventListener('input', (e) => {
                      setNewCard({...newCard, color: e.target.value});
                    });
                    input.click();
                  }}
                  sx={{
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 0,
                    cursor: 'pointer',
                    backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5'
                  }}
                >
                  <ColorIcon />
                </Box>
              </span>
            </Tooltip>
          </Box>
        </>
      </ProModal>

      {/* Beat Color Menu */}
      <Menu
        anchorEl={beatColorMenuAnchorEl}
        open={Boolean(beatColorMenuAnchorEl)}
        onClose={() => setBeatColorMenuAnchorEl(null)}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary
          }
        }}
      >
        <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 1, width: 180 }}>
          {['#f0f0f0', '#ffcccc', '#ccffcc', '#ccccff', '#ffffcc', '#ffccff', '#ccffff'].map(color => (
            <Box
              key={color}
              onClick={() => {
                if (selectedBeat && selectedBeatActIndex !== null && selectedBeatIndex !== null) {
                  handleBeatColorChange(selectedBeatActIndex, selectedBeatIndex, color);
                }
                setBeatColorMenuAnchorEl(null);
              }}
              sx={{
                width: 36,
                height: 36,
                backgroundColor: color,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 0,
                cursor: 'pointer'
              }}
            />
          ))}
          <Box
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'color';
              input.value = selectedBeat ? selectedBeat.color : '#f0f0f0';
              input.addEventListener('change', (e) => {
                if (selectedBeat && selectedBeatActIndex !== null && selectedBeatIndex !== null) {
                  handleBeatColorChange(selectedBeatActIndex, selectedBeatIndex, e.target.value);
                }
                setBeatColorMenuAnchorEl(null);
              });
              input.click();
            }}
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
              cursor: 'pointer',
              backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5'
            }}
          >
            <ColorIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </Menu>
      
      {/* Card Color Menu */}
      <Menu
        anchorEl={colorMenuAnchorEl}
        open={Boolean(colorMenuAnchorEl)}
        onClose={() => setColorMenuAnchorEl(null)}
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary
          }
        }}
      >
        <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 1, width: 180 }}>
          {['#ffffff', '#ffcccc', '#ccffcc', '#ccccff', '#ffffcc', '#ffccff', '#ccffff'].map(color => (
            <Box
              key={color}
              onClick={() => {
                if (selectedCard) {
                  handleChangeCardColor(color);
                }
                setColorMenuAnchorEl(null);
              }}
              sx={{
                width: 36,
                height: 36,
                backgroundColor: color,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 0,
                cursor: 'pointer'
              }}
            />
          ))}
          <Box
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'color';
              input.value = selectedCard ? selectedCard.color : '#ffffff';
              input.addEventListener('change', (e) => {
                if (selectedCard) {
                  handleChangeCardColor(e.target.value);
                }
                setColorMenuAnchorEl(null);
              });
              input.click();
            }}
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
              cursor: 'pointer',
              backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5'
            }}
          >
            <ColorIcon />
          </Box>
        </Box>
      </Menu>

    </Box>
  );
};

export default IndexCards;
